import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Building } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function OrgSettingsPage() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({});
  const [newOrgName, setNewOrgName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');

  const fetchOrgs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/orgs/me`, { withCredentials: true });
      setOrgs(data);
      if (data.length > 0 && !selected) setSelected(data[0]);
    } catch (e) { console.error(e); }
  }, [selected]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  useEffect(() => {
    if (!selected) return;
    setForm(selected);
    axios.get(`${API_URL}/api/orgs/${selected.id}/members`, { withCredentials: true })
      .then(r => setMembers(r.data)).catch(() => setMembers([]));
  }, [selected]);

  const createOrg = async () => {
    if (!newOrgName.trim()) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/orgs`, { name: newOrgName }, { withCredentials: true });
      setNewOrgName('');
      fetchOrgs();
      setSelected(data);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const saveOrg = async () => {
    try {
      await axios.put(`${API_URL}/api/orgs/${selected.id}`, form, { withCredentials: true });
      alert('Saved.');
      fetchOrgs();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const addMember = async () => {
    if (!newMemberEmail.trim()) return;
    try {
      await axios.post(`${API_URL}/api/orgs/${selected.id}/members`, { email: newMemberEmail, role: 'member' }, { withCredentials: true });
      setNewMemberEmail('');
      const r = await axios.get(`${API_URL}/api/orgs/${selected.id}/members`, { withCredentials: true });
      setMembers(r.data);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await axios.delete(`${API_URL}/api/orgs/${selected.id}/members/${uid}`, { withCredentials: true });
      const r = await axios.get(`${API_URL}/api/orgs/${selected.id}/members`, { withCredentials: true });
      setMembers(r.data);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 bg-midnight/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2 text-text-light font-serif flex items-center gap-2">
          <Building className="w-4 h-4" /> Organization settings
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 grid gap-6 md:grid-cols-[260px_1fr]">
        <div className="space-y-3">
          <h3 className="text-text-light font-semibold text-sm">Your organizations</h3>
          {orgs.map(o => (
            <button key={o.id} onClick={() => setSelected(o)}
              className={`w-full text-left px-3 py-2 rounded-lg border ${selected?.id === o.id ? 'border-champagne/40 bg-champagne/5 text-champagne' : 'border-white/10 text-text-light hover:bg-white/5'}`}>
              {o.name}
            </button>
          ))}
          {orgs.length === 0 && <div className="text-text-muted text-xs">No orgs yet</div>}
          <div className="pt-3 border-t border-white/10">
            <input value={newOrgName} onChange={e => setNewOrgName(e.target.value)}
              placeholder="New org name"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-text-light text-sm" />
            <button onClick={createOrg}
              className="mt-2 w-full px-3 py-2 rounded-lg bg-champagne text-midnight text-xs font-medium hover:bg-champagne-light">
              <Plus className="w-3.5 h-3.5 inline mr-1" /> Create
            </button>
          </div>
        </div>

        {selected ? (
          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
              <h3 className="text-text-light font-semibold text-sm mb-3">Brand</h3>
              {['name', 'company_name', 'logo_url', 'custom_footer_text', 'custom_domain'].map(k => (
                <div key={k}>
                  <label className="block text-xs text-text-muted mb-1">{k}</label>
                  <input value={form[k] || ''} onChange={e => setForm({ ...form, [k]: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-text-light text-sm" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                {['primary_color', 'accent_color'].map(k => (
                  <div key={k}>
                    <label className="block text-xs text-text-muted mb-1">{k}</label>
                    <input type="color" value={form[k] || '#000000'} onChange={e => setForm({ ...form, [k]: e.target.value })}
                      className="w-full h-10 rounded-lg bg-white/5 border border-white/10" />
                  </div>
                ))}
              </div>
              <button onClick={saveOrg}
                className="px-4 py-2 rounded-lg bg-champagne text-midnight text-sm font-medium hover:bg-champagne-light">
                Save brand
              </button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <h3 className="text-text-light font-semibold text-sm mb-3">Members ({members.length})</h3>
              <div className="flex gap-2 mb-4">
                <input value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-text-light text-sm" />
                <button onClick={addMember}
                  className="px-3 py-2 rounded-lg bg-aura-purple/20 text-aura-purple text-xs font-medium hover:bg-aura-purple/30">
                  <Plus className="w-3.5 h-3.5 inline mr-1" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {members.map(m => (
                  <div key={m.user_id} className="flex items-center justify-between p-3 rounded-lg border border-white/10">
                    <div>
                      <div className="text-text-light text-sm">{m.name}</div>
                      <div className="text-text-muted text-xs">{m.email} · {m.role}</div>
                    </div>
                    {m.role !== 'owner' && (
                      <button onClick={() => removeMember(m.user_id)}
                        className="p-2 rounded-lg hover:bg-error/10 text-text-muted hover:text-error">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-text-muted">Select or create an organization to manage settings.</div>
        )}
      </div>
      <FooterExpanded />
    </div>
  );
}
